<template>
    <div class="w-full">
        <div class="mb-6 text-center">
            <h2 class="mb-2 text-3xl font-bold text-gray-800">{{ t('pricing.title') }}</h2>
            <p class="text-gray-600">{{ t('pricing.subtitle') }}</p>
        </div>

        <!-- Base Rates Table -->
        <div class="mb-8">
            <h3 class="mb-4 text-xl font-semibold text-gray-800">{{ t('pricing.baseRates') }}</h3>
            <div class="overflow-x-auto">
                <table class="w-full border-collapse overflow-hidden rounded-lg bg-white shadow-sm">
                    <thead>
                        <tr class="bg-primary text-white">
                            <th class="px-6 py-3 text-left">{{ t('pricing.service') }}</th>
                            <th class="px-6 py-3 text-right">{{ t('pricing.basePrice') }}</th>
                            <th class="px-6 py-3 text-right">{{ t('pricing.perExtraPassenger') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="border-b hover:bg-gray-50">
                            <td class="px-6 py-4 font-medium">{{ t('pricing.oneway') }}</td>
                            <td class="px-6 py-4 text-right">{{ pricingDisplay?.baseOneWay || '40€' }}</td>
                            <td class="px-6 py-4 text-right">{{ pricingDisplay?.extraPassengerOneWay || '5€' }}</td>
                        </tr>
                        <tr class="border-b hover:bg-gray-50">
                            <td class="px-6 py-4 font-medium">{{ t('pricing.roundtrip') }}</td>
                            <td class="px-6 py-4 text-right">{{ pricingDisplay?.baseRoundTrip || '80€' }}</td>
                            <td class="px-6 py-4 text-right">{{ pricingDisplay?.extraPassengerRoundTrip || '10€' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Supplements Table -->
        <div class="mb-8">
            <h3 class="mb-4 text-xl font-semibold text-gray-800">{{ t('pricing.supplements') }}</h3>
            <div class="overflow-x-auto">
                <table class="w-full border-collapse overflow-hidden rounded-lg bg-white shadow-sm">
                    <thead>
                        <tr class="bg-primary text-white">
                            <th class="px-6 py-3 text-left">{{ t('pricing.supplementType') }}</th>
                            <th class="px-6 py-3 text-right">{{ t('pricing.amount') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="border-b hover:bg-gray-50">
                            <td class="px-6 py-4 font-medium">{{ t('pricing.orlyAirport') }}</td>
                            <td class="px-6 py-4 text-right">{{ pricingDisplay?.orlySupplement || '15€' }}</td>
                        </tr>
                        <tr class="border-b hover:bg-gray-50">
                            <td class="px-6 py-4 font-medium">{{ nightSupplementText }}</td>
                            <td class="px-6 py-4 text-right">{{ pricingDisplay?.nightSupplement || '20€' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Notes -->
        <div class="mb-8 border-l-4 border-primary bg-blue-50 p-4">
            <h4 class="mb-2 font-semibold text-gray-800">{{ t('pricing.notes.title') }}</h4>
            <ul class="list-inside list-disc space-y-1 text-sm text-gray-700">
                <li>{{ t('pricing.notes.note1') }}</li>
                <li>{{ t('pricing.notes.note2') }}</li>
                <li>{{ t('pricing.notes.note3') }}</li>
            </ul>
        </div>

        <!-- Calculation Examples -->
        <div>
            <h3 class="mb-4 text-xl font-semibold text-gray-800">{{ t('pricing.examples.title') }}</h3>
            <div class="grid gap-4 md:grid-cols-2">
                <div class="rounded-lg border border-green-200 bg-green-50 p-4">
                    <h4 class="mb-2 font-semibold text-green-800">{{ t('pricing.examples.example1.title') }}</h4>
                    <p class="mb-2 text-sm text-gray-700">{{ t('pricing.examples.example1.description') }}</p>
                    <p class="font-bold text-green-800">{{ t('pricing.examples.example1.calculation') }}</p>
                </div>
                <div class="rounded-lg border border-purple-200 bg-purple-50 p-4">
                    <h4 class="mb-2 font-semibold text-purple-800">{{ t('pricing.examples.example2.title') }}</h4>
                    <p class="mb-2 text-sm text-gray-700">{{ t('pricing.examples.example2.description') }}</p>
                    <p class="font-bold text-purple-800">{{ t('pricing.examples.example2.calculation') }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useTranslations } from '@/composables/useTranslations';
import { usePage } from '@inertiajs/vue3';
import { computed } from 'vue';

const { t } = useTranslations();
const page = usePage();

const pricingDisplay = computed(() => (page.props as any).pricingDisplay);

const nightSupplementText = computed(() => {
    const data = pricingDisplay.value || {};
    return t('pricing.nightSupplement', {
        start: data.nightStart || '22h',
        end: data.nightEnd || '6h',
        price: data.nightSupplement || '20€',
    });
});
</script>
